import {
  getRespiratoryPressureVariationStatus,
  getPemaxPositionSeries,
  getPemaxPrimarySeries,
  getPimaxPositionLabel,
  pemaxSeriesAreComparable,
  type PemaxPositionSeries,
  getRespiratorySeverityTone,
  summarizePemaxPayload,
  type PemaxResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';
import {
  getAssessmentInterpretationOverride,
  resolveAssessmentInterpretationText,
} from '../utils/interpretationOverride';

export const getPemaxSeverityLabel = (payload?: PemaxResultPayload | null): string => summarizePemaxPayload(payload).severity;

export const getPemaxSeverityTone = (payload?: PemaxResultPayload | null): string =>
  getRespiratorySeverityTone(summarizePemaxPayload(payload).tone);

export const getPemaxInterpretationText = (payload?: PemaxResultPayload | null): string => {
  const summary = summarizePemaxPayload(payload);
  if (summary.max == null) return 'Não há valor válido de PEmax para interpretar.';
  const percentSegment = summary.percentPredicted != null
    ? `, correspondente a ${summary.percentPredicted.toFixed(1)}% do predito`
    : '';
  const comparisonSegment = summary.lowerLimit != null && summary.belowLowerLimit != null
    ? summary.belowLowerLimit
      ? `, abaixo do limite inferior esperado de ${summary.lowerLimit.toFixed(1)} cmH2O.`
      : `, dentro da faixa esperada (limite inferior de ${summary.lowerLimit.toFixed(1)} cmH2O).`
    : '. O limite inferior esperado não pôde ser determinado com os dados disponíveis.';
  const automaticInterpretation = `A PEmax medida foi de ${summary.max.toFixed(1)} cmH2O${percentSegment}${comparisonSegment}`;
  const professionalOverride = getAssessmentInterpretationOverride(payload);
  if (professionalOverride) return professionalOverride;
  const resolved = resolveAssessmentInterpretationText(
    payload,
    automaticInterpretation,
  );
  return resolved.includes('Resultado em relação ao valor previsto') ? automaticInterpretation : resolved;
};

interface PemaxResultRendererProps {
  payload: PemaxResultPayload | null | undefined;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

const formatPemaxTechnique = (series: PemaxPositionSeries): string => [
  series.technique.initialVolume === 'TLC' ? 'CPT' : series.technique.initialVolume === 'RV' ? 'VR' : series.technique.initialVolume === 'FRC' ? 'CRF' : null,
  series.technique.interface === 'mouthpiece' ? 'Bocal' : series.technique.interface === 'mask' ? 'Máscara' : null,
  series.technique.device,
  series.technique.lipLeak ? 'Fuga labial' : null,
  series.technique.facialSupport ? 'Suporte da face' : null,
  series.technique.cooperationLimited ? 'Cooperação limitada' : null,
  series.technique.orthopnea ? 'Ortopneia' : null,
  series.technique.notes,
].filter(Boolean).join(' · ');

const historyForPemaxSeries = (series: PemaxPositionSeries, history: AssessmentHistoryComparisonEntry[]): AssessmentHistoryComparisonEntry[] =>
  history.flatMap((entry) => {
    const matches = getPemaxPositionSeries(entry.payload as PemaxResultPayload).filter((candidate) => pemaxSeriesAreComparable(series, candidate));
    return matches.length === 1 ? [{ ...entry, payload: matches[0].result }] : [];
  });

export default function PemaxResultRenderer(props: PemaxResultRendererProps) {
  const series = getPemaxPositionSeries(props.payload);
  const primary = getPemaxPrimarySeries(props.payload);
  const hasKnownProtocol = Boolean(props.payload?.positionProtocol || props.payload?.positionSeries?.length);
  const measuredSeries = series.filter((item) => !item.notPerformedReason?.trim());
  const showPredicted = measuredSeries.some((item) => summarizePemaxPayload(item.result).percentPredicted != null);
  const primaryHistory = primary && hasKnownProtocol ? historyForPemaxSeries(primary, props.comparisonHistory ?? []) : [];
  return (
    <div className="space-y-5">
      {series.length > 1 || hasKnownProtocol ? <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-bold text-slate-900">PEmax por posição</h3>
        <table className="mt-4 w-full text-left text-sm">
          <thead><tr><th className="p-2">Posição</th><th className="p-2">PEmax</th>{showPredicted ? <th className="p-2">% do previsto</th> : null}<th className="p-2">Contexto técnico</th></tr></thead>
          <tbody>{series.map((item) => {
            const summary = summarizePemaxPayload(item.result);
            return <tr key={item.id} className="border-t border-slate-100"><td className="p-2 font-medium text-slate-800">{getPimaxPositionLabel(item)}{item.id === primary?.id ? ' · principal' : ''}</td><td className="p-2">{item.notPerformedReason?.trim() ? 'Não realizada' : summary.max != null ? `${summary.max.toFixed(1)} cmH2O` : 'Sem medida'}</td>{showPredicted ? <td className="p-2">{!item.notPerformedReason?.trim() && summary.percentPredicted != null ? `${summary.percentPredicted.toFixed(1)}%` : ''}</td> : null}<td className="p-2 text-slate-600">{item.notPerformedReason?.trim() || formatPemaxTechnique(item)}</td></tr>;
          })}</tbody>
        </table>
        <p className="mt-3 text-xs leading-5 text-slate-500">Cada posição conserva suas tentativas. O resultado principal corresponde à série identificada, sem combinar máximos de posições diferentes.</p>
      </section> : null}
      <SinglePemaxResultRenderer {...props} payload={primary ? { ...primary.result, report: props.payload?.report ?? primary.result.report } : props.payload} comparisonHistory={primaryHistory} />
      {props.comparisonHistory?.length && !primaryHistory.length ? <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">A comparação histórica requer posição e protocolo compatíveis. Os registros sem essas informações ou com condições diferentes permanecem disponíveis no histórico individual.</p> : null}
      {measuredSeries.filter((item) => item.id !== primary?.id).map((item) => <details key={item.id} className="rounded-2xl border border-slate-200 bg-white p-5"><summary className="cursor-pointer text-sm font-semibold text-slate-800">{getPimaxPositionLabel(item)} — resultados e tentativas</summary><div className="mt-5"><SinglePemaxResultRenderer {...props} payload={item.result} comparisonHistory={historyForPemaxSeries(item, props.comparisonHistory ?? [])} /></div></details>)}
    </div>
  );
}

function SinglePemaxResultRenderer({
  payload,
  assessmentDate = null,
  comparisonHistory = [],
}: PemaxResultRendererProps) {
  const summary = summarizePemaxPayload(payload);
  const variationStatus = getRespiratoryPressureVariationStatus(
    summary.quality.variationTop2Percent,
    summary.quality.maneuversAnalyzed,
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-slate-600">PRESSÃO EXPIRATÓRIA MÁXIMA</p>
          {payload?.positionProtocol ? <p className="mb-4 text-sm font-semibold text-slate-600">{getPimaxPositionLabel(payload.positionProtocol)}</p> : null}
          <div className="text-6xl font-black leading-none text-slate-900">{summary.max != null ? summary.max.toFixed(0) : '-'}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">{summary.max != null ? 'cmH2O' : 'sem valor válido'}</div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${getPemaxSeverityTone(payload)}`}>
            {summary.severity}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{getPemaxInterpretationText(payload)}</p>
            <ul className="mt-3 space-y-1 text-xs leading-relaxed text-slate-500">
              {summary.interpretation.lines.filter((line) => !line.startsWith('PEmax ')).map((line, index) => <li key={`pemax-summary-line-${index}`}>{line}</li>)}
            </ul>
          </div>
          {(summary.percentPredicted != null || summary.quality.variationTop2Percent != null) && <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            {summary.percentPredicted != null && <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Percentual do previsto: <span className="font-semibold text-slate-900">{summary.percentPredicted.toFixed(1)}%</span>
            </div>}
            {summary.quality.variationTop2Percent != null && <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Variação entre as 2 melhores manobras:{' '}
              <span className="font-semibold text-slate-900">
                {summary.quality.variationTop2Percent.toFixed(2)}%{variationStatus ? ` (${variationStatus})` : ''}
              </span>
            </div>}
          </div>}
        </div>
      </div>

      {!summary.referenceApplicable ? <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">Resultado descritivo nesta posição e protocolo. A referência normativa adotada considera manobra sentada, iniciada na capacidade pulmonar total e realizada com bocal.</p> : null}

      <AssessmentHistoryComparisonCard
        testKey="PEMAX"
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />

      <div className="grid gap-6 md:grid-cols-3">
        {summary.sustained1s != null && <Metric label="Sustentada por 1 segundo" value={`${summary.sustained1s.toFixed(1)} cmH2O`} />}
        {summary.lowerLimit != null && <Metric label="Limite inferior esperado" value={`${summary.lowerLimit.toFixed(1)} cmH2O`} />}
        <Metric label="Manobras/medidas" value={String(summary.quality.maneuversAnalyzed)} />
      </div>

      {summary.measurements.length ? (
        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Medições manuais</div>
          <div className="mt-3 text-sm text-slate-700">{summary.measurements.map((value) => `${value.toFixed(1)} cmH2O`).join(' - ')}</div>
        </div>
      ) : null}
    </div>
  );
}

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
  </div>
);
